import {
  CUSTOM_PARAMS_STORAGE_KEY,
  DATALAYER_STORAGE_KEY,
  EVENTS_STORAGE_KEY,
  LAST_UPDATE_STORAGE_KEY,
  STORAGE_KEY,
  USER_PROPERTIES_STORAGE_KEY,
  VISIT_ID_STORAGE_KEY,
} from '../../third-party-tracking/resources/constants';
import type {
  StorageTrackingData,
  StorageTrackingParams,
} from '../../third-party-tracking/types';
import { parseJson } from '../../third-party-tracking/utils/objects';
import { createRuntimeState } from '../state';
import type { StorageClient, TrackingStorageClient, Value } from './types';

export const createEmptyTrackingData = (
  visitId: string
): StorageTrackingData => ({
  [VISIT_ID_STORAGE_KEY]: visitId,
  [LAST_UPDATE_STORAGE_KEY]: new Date().getTime(),
  [DATALAYER_STORAGE_KEY]: {},
  [CUSTOM_PARAMS_STORAGE_KEY]: {},
  [USER_PROPERTIES_STORAGE_KEY]: {},
  [EVENTS_STORAGE_KEY]: {},
});

export const createTrackingStorageClient = (
  storageClient: StorageClient
): TrackingStorageClient => {
  const storageClientPromise =
    storageClient.status === 'idle'
      ? storageClient.init()
      : Promise.resolve(storageClient);

  const listenChangeUserConsent = async (callback: () => void) => {
    const storageClient = await storageClientPromise;
    storageClient.listenChangeUserConsent(callback);
  };

  const setInStorageAsync = async <T>(
    type: 'sessionStorage' | 'localStorage',
    key: string,
    value: T
  ) => {
    const storageClient = await storageClientPromise;
    const storageRepository = storageClient.get(type);
    storageRepository.set(key, value);
  };

  const getFromStorageAsync = async <T>(
    type: 'sessionStorage' | 'localStorage',
    key: string,
    fallbackValue?: T
  ): Promise<T> => {
    const storageClient = await storageClientPromise;
    const storageRepository = storageClient.get(type);
    return storageRepository.get(key, fallbackValue);
  };

  /**
   * use only with technical cookies
   */
  const setCookie = (
    name: string,
    value: string | number,
    days: number,
    domain?: string
  ) => {
    const cookieRepository = storageClient.get('cookie');
    cookieRepository.set(name, value, days, domain);
  };

  /**
   * use only with technical cookies
   */
  const getCookie = (key: string) => {
    const cookieRepository = storageClient.get('cookie');
    return cookieRepository.get(key);
  };

  const getTrackingStoredValue = <K extends keyof StorageTrackingData>(
    key: K
  ): StorageTrackingData[K] => {
    const storageRepository = storageClient.get('sessionStorage');
    return storageRepository?.get<StorageTrackingData>(STORAGE_KEY)?.[key];
  };

  /**
   * use only with technical keys
   */
  const update = <K extends keyof StorageTrackingParams>(
    key: K,
    value: Value
  ) => {
    const sessionStorageRepository = storageClient.get('sessionStorage');
    const storedTrackingData =
      sessionStorageRepository.get<StorageTrackingParams>(STORAGE_KEY) ?? {};
    const nextValue =
      typeof value === 'function' ? value(storedTrackingData[key]) : value;
    sessionStorageRepository.set(STORAGE_KEY, {
      ...storedTrackingData,
      [key]: nextValue,
      [LAST_UPDATE_STORAGE_KEY]: new Date().getTime(),
    });
  };

  const initThirdParty = (visitId: string) => {
    if (!visitId) {
      return;
    }
    const sessionStorageRepository = storageClient.get('sessionStorage');
    if (!sessionStorageRepository) {
      return;
    }
    const storedTrackingData: StorageTrackingData =
      sessionStorageRepository.get(STORAGE_KEY) ?? null;
    if (storedTrackingData?.[VISIT_ID_STORAGE_KEY] === visitId) {
      return;
    }
    let openerTrackingData: StorageTrackingData | null = null;
    try {
      const openerStorageItem = (
        window.opener as Window
      )?.sessionStorage?.getItem(STORAGE_KEY);
      if (openerStorageItem) {
        openerTrackingData = parseJson(openerStorageItem);
      }
    } catch (_) {
      // opener access is restricted by CORS
    }
    if (openerTrackingData?.[VISIT_ID_STORAGE_KEY] === visitId) {
      sessionStorageRepository.set(STORAGE_KEY, openerTrackingData);
    } else {
      sessionStorageRepository.set(
        STORAGE_KEY,
        createEmptyTrackingData(visitId)
      );
    }
    const { setParameters } = createRuntimeState();
    setParameters({ isStorageInitialized: true });
  };

  /**
   * use only with technical keys
   */
  const isAllowed = (
    type: 'sessionStorage' | 'localStorage' | 'cookie',
    key: string
  ) => {
    const storageRepository = storageClient.get(type);
    return (storageRepository.get(key) as boolean) || false;
  };

  /**
   * use only with technical keys
   */
  const setInStorage = <T>(
    type: 'sessionStorage' | 'localStorage',
    key: string,
    value: T
  ) => {
    const sessionStorageRepository = storageClient.get(type);
    sessionStorageRepository.set(key, value);
  };

  /**
   * use only with technical keys
   */
  const getFromStorage = <T>(
    type: 'sessionStorage' | 'localStorage',
    key: string,
    fallbackValue?: T
  ): T => {
    const storageRepository = storageClient.get(type);
    return storageRepository.get(key, fallbackValue);
  };

  const removeFromStorage = (
    type: 'sessionStorage' | 'localStorage',
    key: string
  ) => {
    const storageRepository = storageClient.get(type);
    return storageRepository.delete(key);
  };

  const removeCookie = (key: string) => {
    const cookieRepository = storageClient.get('cookie');
    return cookieRepository.delete(key);
  };

  return {
    initThirdParty,
    isAllowed,
    getTrackingStoredValue,
    update,
    getCookie,
    listenChangeUserConsent,
    setInStorage,
    getFromStorage,
    removeFromStorage,
    setCookie,
    setInStorageAsync,
    getFromStorageAsync,
    removeCookie,
  };
};
